// src/pages/NotificationSettings.jsx
// Admin-only: push notification status/test + the editable birthday email
// message. Gated both by the sidebar (only System Admin/President ever see
// the nav link) and by the backend (notification_admin.php /
// birthday_email_settings.php both require an admin-role session).
import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { FaBell, FaBirthdayCake, FaPaperPlane, FaUndo, FaSave, FaUserShield, FaTrash, FaPlus } from "react-icons/fa";
import { authFetch, getCurrentUser } from "../utils/api";
import { OFFICER_ROLES } from "../config/roles";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

// Suggestions only (the <input> accepts any role string, matching however
// it's actually spelled in MemberBasic.Role) -- OFFICER_ROLES is the same
// list already used elsewhere in the app to decide who's an "officer".
const KNOWN_ROLE_SUGGESTIONS = [...new Set(OFFICER_ROLES)];

export default function NotificationSettings() {
  const user = getCurrentUser() || { name: "Unknown User", role: "Member", image: "/user.png" };

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [messageBody, setMessageBody] = useState("");
  const [defaultMessage, setDefaultMessage] = useState("");
  const [loadingMessage, setLoadingMessage] = useState(true);
  const [savingMessage, setSavingMessage] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  const [roleRows, setRoleRows] = useState([]);
  const [defaultPushRoles, setDefaultPushRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [newRoleName, setNewRoleName] = useState("");
  const [savingRole, setSavingRole] = useState("");

  const loadRoleSettings = () => {
    setLoadingRoles(true);
    authFetch(`${ABSOLUTE_HOST}/Backend/notification_role_settings.php`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) {
          setRoleRows(d.rows || []);
          setDefaultPushRoles(d.default_push_roles || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingRoles(false));
  };

  useEffect(() => { loadRoleSettings(); }, []);

  const saveRoleRow = async (roleName, getsPush, getsEmail) => {
    if (!roleName.trim()) return;
    setSavingRole(roleName);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/notification_role_settings.php`, {
        method: "POST",
        body: JSON.stringify({ role_name: roleName.trim(), gets_push: getsPush, gets_email: getsEmail }),
      });
      const data = await res.json();
      if (data?.success) {
        setRoleRows((prev) => {
          const others = prev.filter((r) => r.RoleName !== roleName.trim());
          return [...others, { RoleName: roleName.trim(), GetsPush: getsPush ? 1 : 0, GetsEmail: getsEmail ? 1 : 0 }]
            .sort((a, b) => a.RoleName.localeCompare(b.RoleName));
        });
        setNewRoleName("");
      }
    } catch (e) {
      // non-critical -- row just won't update visually
    } finally {
      setSavingRole("");
    }
  };

  const removeRoleRow = async (roleName) => {
    setSavingRole(roleName);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/notification_role_settings.php?role_name=${encodeURIComponent(roleName)}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data?.success) {
        setRoleRows((prev) => prev.filter((r) => r.RoleName !== roleName));
      }
    } catch (e) {
      // non-critical
    } finally {
      setSavingRole("");
    }
  };

  const loadStats = () => {
    setLoadingStats(true);
    authFetch(`${ABSOLUTE_HOST}/Backend/notification_admin.php`)
      .then((r) => r.json())
      .then((d) => { if (d?.success) setStats(d); })
      .catch(() => {})
      .finally(() => setLoadingStats(false));
  };

  useEffect(() => {
    loadStats();
    authFetch(`${ABSOLUTE_HOST}/Backend/birthday_email_settings.php`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) {
          setMessageBody(d.message_body || "");
          setDefaultMessage(d.message_body || "");
          setUpdatedAt(d.updated_at);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMessage(false));
  }, []);

  const sendTest = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/notification_admin.php`, {
        method: "POST",
        body: JSON.stringify({ action: "send_test" }),
      });
      const data = await res.json();
      setTestResult({ ok: !!data.success, message: data.message || (data.success ? "Sent." : "Failed.") });
    } catch (e) {
      setTestResult({ ok: false, message: "Network error while sending test notification." });
    } finally {
      setSendingTest(false);
    }
  };

  const saveMessage = async () => {
    if (!messageBody.trim()) return;
    setSavingMessage(true);
    setSaveResult(null);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/birthday_email_settings.php`, {
        method: "POST",
        body: JSON.stringify({ message_body: messageBody.trim(), updated_by_sliit_id: user?.SLIITID || "" }),
      });
      const data = await res.json();
      setSaveResult({ ok: !!data.success, message: data.message || (data.success ? "Saved." : "Failed to save.") });
      if (data.success) setUpdatedAt(new Date().toISOString());
    } catch (e) {
      setSaveResult({ ok: false, message: "Network error while saving." });
    } finally {
      setSavingMessage(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div className="page-head">
            <div>
              <h1>Notification Settings</h1>
              <p className="page-desc">Push notification status and the editable birthday email message. Admins only.</p>
            </div>
          </div>

          {/* Push notifications */}
          <div className="panel" style={{ padding: "var(--sp-4)", marginBottom: "var(--sp-6)" }}>
            <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaBell size={14} style={{ color: "var(--accent)" }} /> Mobile Push Notifications
              </h2>
            </div>

            {loadingStats ? (
              <div className="empty-state"><span className="spinner lg" /></div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)", marginBottom: "var(--sp-4)" }}>
                <div className="panel tile">
                  <span className="tile-label">Total Subscribed Devices</span>
                  <span className="tile-value num">{stats?.total_subscriptions ?? "—"}</span>
                </div>
                <div className="panel tile">
                  <span className="tile-label">Your Subscribed Devices</span>
                  <span className="tile-value num">{stats?.my_subscriptions ?? "—"}</span>
                </div>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button onClick={sendTest} disabled={sendingTest} className="btn btn-primary btn-sm">
                {sendingTest ? <span className="spinner" /> : <FaPaperPlane size={11} />}
                {sendingTest ? "Sending…" : "Send test notification to me"}
              </button>
              <button onClick={loadStats} className="btn btn-ghost btn-sm">
                <FaUndo size={11} /> Refresh stats
              </button>
            </div>
            {testResult && (
              <div className={testResult.ok ? "success-banner" : "error-banner"} style={{ marginTop: "var(--sp-3)" }}>
                {testResult.message}
              </div>
            )}
            <p className="field-hint" style={{ marginTop: "var(--sp-3)" }}>
              Sends only to your own account's subscribed device(s) -- never broadcasts to other members.
            </p>
          </div>

          {/* Birthday email */}
          <div className="panel" style={{ padding: "var(--sp-4)" }}>
            <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaBirthdayCake size={14} style={{ color: "var(--accent)" }} /> Birthday Email Message
              </h2>
            </div>

            {loadingMessage ? (
              <div className="empty-state"><span className="spinner lg" /></div>
            ) : (
              <>
                <div className="field">
                  <label className="field-label" htmlFor="birthday-message">
                    Message shown in the birthday wish email (subject, layout, and signature stay fixed)
                  </label>
                  <textarea
                    id="birthday-message"
                    rows={5}
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    maxLength={4000}
                  />
                  <p className="field-hint">{messageBody.length}/4000 characters{updatedAt ? ` · Last updated ${new Date(updatedAt).toLocaleString()}` : ""}</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button onClick={saveMessage} disabled={savingMessage || !messageBody.trim()} className="btn btn-primary btn-sm">
                    {savingMessage ? <span className="spinner" /> : <FaSave size={11} />}
                    {savingMessage ? "Saving…" : "Save message"}
                  </button>
                  <button
                    onClick={() => setMessageBody(defaultMessage)}
                    disabled={savingMessage}
                    className="btn btn-ghost btn-sm"
                  >
                    Reset to current saved value
                  </button>
                </div>
                {saveResult && (
                  <div className={saveResult.ok ? "success-banner" : "error-banner"} style={{ marginTop: "var(--sp-3)" }}>
                    {saveResult.message}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Per-role notification routing */}
          <div className="panel" style={{ padding: "var(--sp-4)", marginTop: "var(--sp-6)" }}>
            <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaUserShield size={14} style={{ color: "var(--accent)" }} /> Who Gets Notified
              </h2>
            </div>
            <p className="field-hint" style={{ marginBottom: "var(--sp-4)" }}>
              Controls both birthday and event reminders. <b style={{ color: "var(--text-1)" }}>Push</b> = this role
              receives the admin push notification. <b style={{ color: "var(--text-1)" }}>Email</b> = a member with
              this role gets the automated birthday email on their birthday. Leave empty to use the default (Push:{" "}
              {defaultPushRoles.join(", ") || "System Admin, President"}; Email: everyone except Club Member).
            </p>

            {loadingRoles ? (
              <div className="empty-state"><span className="spinner lg" /></div>
            ) : (
              <>
                <div className="overflow-x-auto" style={{ marginBottom: "var(--sp-4)" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Role</th>
                        <th style={{ textAlign: "center" }}>Push</th>
                        <th style={{ textAlign: "center" }}>Email</th>
                        <th style={{ textAlign: "right" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleRows.length === 0 ? (
                        <tr><td colSpan={4} style={{ color: "var(--text-3)" }}>No roles configured yet -- using defaults for everyone.</td></tr>
                      ) : (
                        roleRows.map((row) => (
                          <tr key={row.RoleName}>
                            <td style={{ fontWeight: 600 }}>{row.RoleName}</td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={!!Number(row.GetsPush)}
                                disabled={savingRole === row.RoleName}
                                onChange={(e) => saveRoleRow(row.RoleName, e.target.checked, !!Number(row.GetsEmail))}
                                aria-label={`${row.RoleName} gets push`}
                              />
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={!!Number(row.GetsEmail)}
                                disabled={savingRole === row.RoleName}
                                onChange={(e) => saveRoleRow(row.RoleName, !!Number(row.GetsPush), e.target.checked)}
                                aria-label={`${row.RoleName} gets email`}
                              />
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                onClick={() => removeRoleRow(row.RoleName)}
                                disabled={savingRole === row.RoleName}
                                className="icon-btn"
                                title="Remove (reverts to default)"
                                aria-label={`Remove ${row.RoleName} override`}
                              >
                                <FaTrash size={12} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="Role name (e.g. Secretary)"
                    aria-label="Role name to add"
                    list="known-roles"
                    style={{ maxWidth: 260 }}
                  />
                  <datalist id="known-roles">
                    {KNOWN_ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
                  </datalist>
                  <button
                    onClick={() => saveRoleRow(newRoleName, true, true)}
                    disabled={!newRoleName.trim() || savingRole === newRoleName.trim()}
                    className="btn btn-primary btn-sm"
                  >
                    <FaPlus size={11} /> Add (Push + Email)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
